import 'gsap';
import $ from '@vaersaagod/tools/Dom';
import * as eventKeys from '../lib/events';
import Dispatch from '@vaersaagod/tools/Dispatch';
import Components from '@vaersaagod/tools/Components';
import Viewport from '@vaersaagod/tools/Viewport';
import { deferredCallback } from '@vaersaagod/tools/utils';

let $body;

let windowHeight = 0;
let defaultElementDelaySpacing = 0.25;
let boxUpYOffset = 100; // updates according to window height
let boxDownYOffset = 100; // updates according to window height
let showThreshold = 20;
let animationElements = [];

const onScroll = () => {
    scrollCheck(Viewport.scrollTop);
};

const onResize = () => {
    windowHeight = Viewport.size.height;
    scrollCheck(Viewport.scrollTop);
};

const init = () => {
    $body = $('body');

    $body.find('[data-animator-element]').each(function (el, i) {
        animationElements.push($(el));
    });

    window.addEventListener('scroll', deferredCallback(onScroll, 10));

    windowHeight = Viewport.size.height;
    boxUpYOffset = Math.round(windowHeight / 10);
    boxDownYOffset = -Math.round(windowHeight / 12);

    resetElements(animationElements);
    scrollCheck(Viewport.scrollTop);

    Viewport.on('resize', onResize);
};

const scrollCheck = scrollTop => {
    const elems = getHiddenElementsInViewport(scrollTop);
    animateElementsIn(elems);

    const elemsBelow = getAnimatedElementsBelowViewport(scrollTop);
    resetElements(elemsBelow);
};

const getHiddenElementsInViewport = scrollTop => {
    let r = [];

    animationElements.forEach(($element, i) => {
        if (!$element.data('was-animated')) {
            let element_top_position = $element.offset().top;

            if (element_top_position <= windowHeight + scrollTop - showThreshold) {
                r.push($element);
            }
        }
    });

    return r;
};

const getAnimatedElementsBelowViewport = scrollTop => {
    let r = [];

    animationElements.forEach(($element, i) => {
        if ($element.data('was-animated')) {
            let element_top_position = $element.offset().top;

            if (element_top_position > windowHeight + scrollTop) {
                r.push($element);
            }
        }
    });

    return r;
};

const getIsElementAbove = $element => {
    const scrollTop = Viewport.scrollTop;
    const elementTopPosition = $element.offset().top;
    const elementHeight = $element.height();
    
    return (elementTopPosition+elementHeight < scrollTop);
};

const resetElements = elements => {
    elements.forEach(($element, i) => {
        let animType = $element.data('animator-element').split(',');

        animType.forEach(function (val, i) {
            resetAnimationType(val, $element);
        });

        $element.data('was-animated', false);
    });
};

const resetAnimationType = (type, $element) => {
    switch (type) {
        case 'fade':
            TweenLite.set($element.get(0), { opacity: 0 });
            break;
        case 'caseblock':
            resetCaseBlockAnimation($element);
            break;
        case 'themeblock':
            resetThemeBlockAnimation($element);
            break;
    }
};

const animateElementsIn = elements => {
    let delay = 0;

    elements.forEach((el, i) => {
        let $element = $(el);
        let isElementAbove = getIsElementAbove($element);
        let animType = $element.data('animator-element').split(',');

        animType.forEach(function (val, i) {
            animateType(val, $element, delay, isElementAbove);
            
            if (!isElementAbove) {
                delay += ((animType + '') === 'fade' ? 0.12 : defaultElementDelaySpacing);
            }
        });

        $element.data('was-animated', true);
    });
};

const animateType = (type, $element, delay, fastForward) => {
    switch (type) {
        case 'fade':
            if (!fastForward) {
                TweenLite.to($element.get(0), 1, { delay: delay, opacity: 1, ease: Sine.easeOut });
            } else {
                TweenLite.set($element.get(0), { opacity: 1 });
            }
            break;
        case 'caseblock':
            doCaseBlockAnimation($element, delay, fastForward);
            break;
        case 'themeblock':
            doThemeBlockAnimation($element, delay, fastForward);
            break;
    }
};

const doCaseBlockAnimation = ($element, delay, fastForward) => {
    let $imageBlocks = $element.find('[data-animator-element-image]');
    let $text = $element.find('[data-animator-element-text]');

    if (!fastForward) {
        TweenLite.to($imageBlocks.nodes, 1.4, { delay: delay, y: 0, ease: Expo.easeOut });
        TweenLite.to($imageBlocks.nodes, 0.8, { delay: delay, opacity: 1, ease: Sine.easeOut });
        TweenLite.to($text.nodes, 0.5, { delay: delay + 0.7, opacity: 1, ease: Sine.easeOut });
    } else {
        TweenLite.set($imageBlocks.nodes, { y: 0 });
        TweenLite.set($imageBlocks.nodes, { opacity: 1 });
        TweenLite.set($text.nodes, { opacity: 1 });
    }
};

const resetCaseBlockAnimation = ($element) => {
    let $imageBlocks = $element.find('[data-animator-element-image]');
    let $text = $element.find('[data-animator-element-text]');

    TweenLite.set($imageBlocks.nodes, { opacity: 0, y: boxUpYOffset });
    TweenLite.set($text.nodes, { opacity: 0 });
};


const doThemeBlockAnimation = ($element, delay, fastForward) => {
    let $bg = $element.find('[data-animator-element-bg]');
    let $textWrap = $element.find('[data-animator-element-textwrap]');
    let $imageWrap = $element.find('[data-animator-element-imagewrap]');

    if (!fastForward) {
        TweenLite.to($bg.nodes, 1, { delay: delay, scaleY: 1, ease: Quint.easeOut });

        $imageWrap.each((el, i) => {
            TweenLite.to(el, 0.5, { delay: delay + 0.8, opacity: 1, ease: Sine.easeOut });
        });

        TweenLite.to($textWrap.nodes, 0.5, { delay: delay + 1, opacity: 1, ease: Sine.easeOut });
    } else {
        TweenLite.set($bg.nodes, { scaleY: 1 });

        $imageWrap.each((el, i) => {
            TweenLite.set(el, { opacity: 1 });
        });

        TweenLite.set($textWrap.nodes, { opacity: 1 });
    }
};

const resetThemeBlockAnimation = ($element) => {
    let $bg = $element.find('[data-animator-element-bg]');
    let $textWrap = $element.find('[data-animator-element-textwrap]');
    let $imageWrap = $element.find('[data-animator-element-imagewrap]');

    TweenLite.set($bg.nodes, { scaleY: 0, transformOrigin: '50% 50%' });
    TweenLite.set($imageWrap.nodes, { opacity: 0 });
    TweenLite.set($textWrap.nodes, { opacity: 0 });
};


// Public api for module
export default {
    init
};



// WEBPACK FOOTER //
// ./src/js/modules/Animator.js